import React from "react";
import { RedactedPaymentPref } from "../../common/types";
import { getPaymentMethod } from "../../common/utils";
import { ExistingPaymentPrefDisplay } from "../ExistingPaymentPrefDisplay";
import { PaymentPrefActions } from "../PaymentPrefActions";
import { PaymentPrefDisplayCardContainer } from "../PaymentPrefDisplayCardContainer";
import { getPaymentMethodIconType } from "../PaymentPrefDisplayCardContainer/utils";

export type ExistingPaymentPrefDisplayCardProps = {
  /** Existing payment preference data to display */
  paymentPref: RedactedPaymentPref;
  /** Whether to show the list of positions using this payment preference */
  displayPositions?: boolean;
  /** Whether to show the reveal/hide details button */
  displayRevealDetails?: boolean;
  /** Whether to show warning for invalid payment instructions */
  displayInvalidInstructionsWarning?: boolean;

  /** Callback functions for payment preference actions */
  callBacks?: {
    switchPaymentMethodCallback: () => void;
    editPaymentInstructionsCallback: () => void;
    deletePaymentInstructionsCallback: () => void;
  };
};

/**
 * Card component for displaying existing payment preference information.
 *
 * This component wraps ExistingPaymentPrefDisplay in a card container with an icon and optional
 * action buttons. For advanced features like accessory content, field visibility configuration,
 * and default expanded state, use ExistingPaymentPrefDisplay directly.
 */
export const ExistingPaymentPrefDisplayCard: React.FC<ExistingPaymentPrefDisplayCardProps> = ({
  paymentPref,
  displayPositions,
  displayRevealDetails,
  callBacks,
  displayInvalidInstructionsWarning,
}: ExistingPaymentPrefDisplayCardProps) => {
  const actionComponent = callBacks && (
    <PaymentPrefActions
      paymentMethod={getPaymentMethod(paymentPref)}
      switchPaymentMethodCallback={callBacks?.switchPaymentMethodCallback}
      editPaymentInstructionsCallback={callBacks?.editPaymentInstructionsCallback}
      deletePaymentInstructionsCallback={callBacks?.deletePaymentInstructionsCallback}
    />
  );

  const iconType = getPaymentMethodIconType(paymentPref);

  return (
    <PaymentPrefDisplayCardContainer iconType={iconType} actionComponent={actionComponent}>
      <ExistingPaymentPrefDisplay
        displayPositions={displayPositions}
        displayRevealDetails={displayRevealDetails}
        paymentPref={paymentPref}
        displayInvalidInstructionsWarning={displayInvalidInstructionsWarning}
      />
    </PaymentPrefDisplayCardContainer>
  );
};
